/**
 * RecommendationFacade.ts - Interface unificada para o sistema de recomendação
 * Este arquivo fornece uma fachada para os diversos serviços de recomendação,
 * ocultando a complexidade interna e apresentando uma API simples e consistente.
 */

import { 
  Recommendation,
  RecommendationRequest, 
  Game,
  GameStats,
  RecommendationAlgorithm,
  RecommendationFilters
} from '../types';

import { AlgorithmSelector } from './services/algorithmSelector'; // Corrigido
import { RecommendationStorage } from './services/recommendationStorage';
import { RecommendationScorer } from './services/recommendationScorer'; // Corrigido
import { RecommendationMonitor } from './services/recommendationMonitor';
import { UserGameService } from './services/userGameService'; 
import { CacheService } from './services/cacheService';
import { RecommendationFilterBuilder } from './services/recommendationFilterBuilder'; // Corrigido
import { AdaptiveRecommender } from './recommenders/adaptiveRecommender';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from './supabase';
import { RecommendationDataService } from './recommenders/services/recommendationDataService'; // Corrected path
import { POPULAR_RECOMMENDATION_CONFIG, RECOMMENDATION_CONFIG } from './recommenders/index';
import { PopularRecommendationService } from './services/popularRecommendationService'; // NOVO IMPORT
import { RecommendationFeedbackService } from './services/recommendationFeedbackService'; // NOVO IMPORT
import { RecommendationOrchestratorService } from './services/recommendationOrchestratorService'; // NOVO IMPORT
import { UserInteractionDataService } from './services/userInteractionDataService'; // NOVO IMPORT

/**
 * Interface para recomendações com razão estruturada
 */
interface RecommendationWithReason {
  id: string;
  user_id: string;
  game_id: string;
  score: number;
  reason: {
    type: string;
    score: number;
  };
  algorithm?: RecommendationAlgorithm;
  viewed: boolean;
  liked: boolean | null;
  created_at?: string;
  updated_at?: string;
  game?: Game;
}

/**
 * Fachada (Facade) para o sistema de recomendação que orquestra os serviços existentes
 * sem reimplementar suas funcionalidades.
 * 
 * Implementa o padrão Facade para orquestrar os diversos serviços especializados
 * já existentes na pasta services/, evitando duplicação de código e
 * seguindo o princípio de responsabilidade única.
 */
export class RecommendationFacade {
  private adaptiveRecommender: AdaptiveRecommender;
  private cacheExpirationTime = 15 * 60 * 1000; // 15 minutos
  private popularRecommendationService: PopularRecommendationService;
  private feedbackService: RecommendationFeedbackService;
  private orchestratorService: RecommendationOrchestratorService;
  private userInteractionDataService: UserInteractionDataService;
  private userGameService: UserGameService;
  
  constructor(
    private algorithmSelector: AlgorithmSelector = new AlgorithmSelector(),
    private storage: RecommendationStorage = new RecommendationStorage(),
    private scorer: RecommendationScorer = new RecommendationScorer(),
    private monitor: RecommendationMonitor = new RecommendationMonitor(),
    userGameService?: UserGameService,
    private cacheService: CacheService = new CacheService(),
    private filterBuilder: RecommendationFilterBuilder = new RecommendationFilterBuilder(),
    popularRecommendationService?: PopularRecommendationService, 
    feedbackService?: RecommendationFeedbackService,
    orchestratorService?: RecommendationOrchestratorService,
    userInteractionDataService?: UserInteractionDataService
  ) {
    // Logs simplificados para inicialização
    console.log('[Facade constructor] Initializing dependencies...');
    this.adaptiveRecommender = new AdaptiveRecommender();
    this.popularRecommendationService = popularRecommendationService || new PopularRecommendationService();
    this.feedbackService = feedbackService || new RecommendationFeedbackService(this.storage, this.monitor, this.cacheService);
    this.userInteractionDataService = userInteractionDataService || new UserInteractionDataService(this.cacheService);
    console.log('[Facade constructor] userInteractionDataService initialized:', this.userInteractionDataService ? 'Instance OK' : 'Instance FAILED');
    
    // Instanciar UserGameService APÓS UserInteractionDataService, injetando-o
    this.userGameService = userGameService || new UserGameService(this.userInteractionDataService);
    console.log('[Facade constructor] userGameService initialized:', this.userGameService ? 'Instance OK' : 'Instance FAILED');

    this.orchestratorService = orchestratorService || new RecommendationOrchestratorService(
      this.adaptiveRecommender, 
      this.popularRecommendationService, 
      this.cacheService, 
      this.storage, 
      this.monitor, 
      this.userInteractionDataService
    ); 
    console.log('[Facade constructor] orchestratorService initialized:', this.orchestratorService ? 'Instance OK' : 'Instance FAILED');
  }

  /**
   * Obtém recomendações para um usuário
   * Retorna um objeto com listas separadas para personalizadas e populares
   */
  public async getRecommendations(
    userId: string,
    forcePersonalized: boolean = false
  ): Promise<{ personalized: Recommendation[], popular: Recommendation[] }> {
    return this.orchestratorService.orchestrateRecommendations(userId, forcePersonalized);
  }
  
  /**
   * Marca uma recomendação como visualizada
   */
  public async markRecommendationAsViewed(recommendationId: string): Promise<void> {
    try {
      console.log(`[Facade] Marking recommendation ${recommendationId} as viewed via Storage.`);
      await this.storage.markRecommendationAsViewed(recommendationId);
      console.log(`[Facade] Recommendation ${recommendationId} marked as viewed successfully via Storage.`);
    } catch (error) {
      // Corrigindo a formatação do log de erro
      console.error(`[Facade] Erro ao marcar recomendação ${recommendationId} como visualizada: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  
  /**
   * Dá feedback (curtiu/não curtiu) para uma recomendação específica.
   * Atualiza o estado no RecommendationStorage e invalida o cache.
   * Permite opcionalmente fornecer uma recomendação temporária se ela ainda não existe no banco.
   * @param recommendationId O ID da recomendação.
   * @param liked O estado do feedback: true para curtiu, false para não curtiu, null para remover feedback.
   * @param temporaryRecommendation Dados da recomendação temporária (opcional).
   * @returns A recomendação atualizada ou a temporária salva, ou null em caso de erro.
   */
  public async giveRecommendationFeedback(
    recommendationId: string,
    liked: boolean | null,
    temporaryRecommendation?: Recommendation | null
  ): Promise<Recommendation | null> {
    return this.feedbackService.giveRecommendationFeedback(recommendationId, liked, temporaryRecommendation);
  }

  /**
   * Retorna as recomendações com feedback (curtidas ou não curtidas) de um usuário
   * @param filters Filtros para obter recomendações com feedback
   * @returns Lista de recomendações com feedback
   */
  async getFeedbackRecommendations(filters: {userId?: string; liked?: boolean} = {}): Promise<Recommendation[]> {
    // Delegar para o RecommendationFeedbackService
    return this.feedbackService.getFeedbackRecommendations(filters);
  }

  /**
   * Remove feedback de uma recomendação específica
   * @param recommendationId ID da recomendação
   * @returns A recomendação atualizada ou null em caso de erro
   */
  public async removeFeedback(recommendationId: string): Promise<Recommendation | null> {
    try {
      if (!recommendationId) {
        console.log('[Facade] removeFeedback: ID de recomendação não fornecido');
        return null;
      }
      console.log(`[Facade] Removendo feedback para a recomendação ${recommendationId} via FeedbackService.`);
      // Usa o método giveRecommendationFeedback do feedbackService com liked = null
      return await this.feedbackService.giveRecommendationFeedback(recommendationId, null);
    } catch (error) {
      console.error(`[Facade] Erro ao remover feedback: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }
}